import { createRef, PureComponent } from 'react';
import PropTypes from 'prop-types';
import html2pdf from 'html2pdf.js/dist/html2pdf.min';
import { withStyles } from '@material-ui/core';
import classNames from 'classnames';
import UsersComments from '../../shared/usersComments';
import CustomButton from '../../shared/customButton';
import SectionTitle from '../../shared/sectionTitle';
import OneOnOneRecordDetails from '../../shared/oneOnOneRecordDetails';
import AlertDialog from '../../shared/alertDialog';
import ManageOneOnOne from '../../shared/manageOneOnOne';
import ManageTasks from '../../shared/manageTasks';
import NotificationCard from '../../shared/notificationCard';
import RichTextPreview from '../../shared/richTextPreview';
import ActionButton from '../../shared/actionButton';
import Tooltip from '../../shared/tooltip';
import AddImpressionDialog from '../../shared/addImpressionDialog';
import UpdatesInfo from '../../shared/updatesInfo';
import CustomFormDrawer from '../../shared/customFormDrawer';
import UserCount from '../../shared/userCount';
import { ReactComponent as PreviewUserIcon } from '../../../assets/icons/remove_red_eye.svg';
import { ReactComponent as AnonymousIcon } from '../../../assets/icons/anonymous-user.svg';
import {
  api_one_on_one_comment_update,
  api_one_on_one_comments,
  api_one_on_one_record,
  api_one_on_one_topic,
  api_one_on_one_record_agenda,
  API_ONE_ON_ONE_TOPICS,
  api_user_one_on_one,
  api_one_on_one_tasks,
  api_one_on_one_with_notes_view_permission,
} from '../../../constants/apiRoutes';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { GENERAL_CATEGORY_ID } from '../../../constants/tags';
import {
  DEFAULT_PARAMS,
  INITIAL_TOPIC_DATA,
  RECURRING_TYPES,
  DATE_TIME_FORMAT,
} from '../../../constants/oneOnOne';
import { PARAMS } from '../../../constants/pages';
import {
  getObjectToNumberArray,
  isUserDeactivated,
  isValidParameter,
  replacePropsInObject,
  isArrayEmpty,
} from '../../../utility/helpers';
import http from '../../../utility/http';
import {
  EVENT_ACTION_TYPES,
  tagManagerDataLayer,
} from '../../../utility/tagManager';
import { showSuccessMessage } from '../../../utility/uiUtils';
import {
  prepareManageOneOnOneSuccessMessages,
  prepareOneOnOneTasksAvailableUsers,
  prepareUserTopicSidebarTranslations,
  prepareUserTopicFields,
  prepareUserTopicData,
} from '../../../utility/oneOnOne';
import {
  hasScopedAuthPermissionCheck,
  isUserProfileAccessibleCheck,
} from '../../../utility/permissions';
import { formatDate, isDateInPast } from '../../../utility/dateUtils';
import { onSaveCreatableTag } from '../../../utility/tagUtils';
import { prepareNotificationUpdates } from '../../../utility/globalNotifications';
import { prepareTasksForSave } from '../../../utility/tasks';
import { ACTION_BUTTON_TYPES } from '../../shared/actionButton/config';

const styles = ({ palette: { primary }, spacing }) => ({
  heading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 30,
    margin: spacing(8, 0, 6, 0),
  },
  subtitle: {
    margin: spacing(8, 0, 4, 0),
  },
  notes: {
    position: 'relative',
    boxSizing: 'border-box',
    border: `1px solid ${primary.bluish6}`,
    padding: spacing(8),
    minHeight: 150,
  },
  editDescriptionButton: {
    position: 'absolute',
    top: 16,
    right: 16,
  },
  noteLabelWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  privateNoteIcon: {
    marginTop: spacing(3),
    marginLeft: spacing(2),
    width: 14,
    height: 14,
  },
  loadMoreContainer: {
    marginLeft: spacing(16),
  },
  loadMore: {
    border: `1px solid ${primary.bluish3}`,
    borderRadius: 44,
    color: primary.bluish4,
    lineHeight: '24px',
    justifySelf: 'center',
    padding: spacing(1, 4),
    maxHeight: 32,
  },
  userCount: {
    marginLeft: spacing(2),
  },
  downloadButtonWrapper: {
    display: 'flex',
    marginLeft: 'auto',
  },
  downloadButton: {
    width: 24,
    height: 24,
  },
});

class OneOnOneDetailsPage extends PureComponent {
  constructor(props) {
    super(props);

    this.contentRef = createRef();
  }

  state = {
    isLoading: true,
    isManageRecordOpened: false,
    isManageTasksOpened: false,
    isEmployeeTopicOpened: false,
    isDeleteEmployeeTopicOpened: false,
    employeeTopic: INITIAL_TOPIC_DATA,
    isDeleteRecordOpened: false,
    isReschedule: false,
    newRecurring: false,
    isAddImpressionOpened: false,
    page: 1,
    commentId: 0,
  };

  componentDidMount() {
    const {
      location,
      matchedOneOnOneId,
      auth,
      navigate,
      getOneOnOneRecord,
      getOneOnOneTopics,
      getTagsCategories,
      getOneOnOneComments,
    } = this.props;
    const isSuggestTopicDrawerOpened =
      location?.search?.includes('?suggestTopic=true');

    if (isValidParameter(matchedOneOnOneId)) {
      return Promise.all([
        getOneOnOneComments(matchedOneOnOneId, DEFAULT_PARAMS),
        getOneOnOneRecord(matchedOneOnOneId),
      ]).then(() => {
        const { oneOnOneRecord } = this.props;

        if (isSuggestTopicDrawerOpened) {
          this.setState({
            isEmployeeTopicOpened: true,
            employeeTopic: prepareUserTopicData(oneOnOneRecord, auth.id),
          });
          navigate(`/1-1/${oneOnOneRecord.id}/details`, { replace: true });
        }

        if (oneOnOneRecord.moderator?.id === auth.id) {
          return Promise.all([getTagsCategories(), getOneOnOneTopics()]).then(
            () => this.setState({ isLoading: false })
          );
        }
        return this.setState({ isLoading: false });
      });
    }
  }

  componentWillUnmount() {
    const {
      clearOneOnOneRecord,
      clearOneOnOneTopics,
      clearTagsCategories,
      clearOneOnOneComments,
    } = this.props;

    clearOneOnOneRecord();
    clearOneOnOneTopics();
    clearTagsCategories();
    clearOneOnOneComments();
  }

  onRedirectToRecordsPage = () => {
    const { navigate } = this.props;

    navigate('/1-1', { replace: true });
  };

  toggleManageRecord =
    (isReschedule = false) =>
    () => {
      this.setState(prevState => ({
        isManageRecordOpened: !prevState.isManageRecordOpened,
        isReschedule,
        newRecurring: false,
      }));
    };

  onOpenEmployeeTopic = () => {
    const { auth, oneOnOneRecord } = this.props;
    this.setState({
      isEmployeeTopicOpened: true,
      employeeTopic: prepareUserTopicData(oneOnOneRecord, auth.id),
    });
  };

  onCloseEmployeeTopic = () =>
    this.setState({
      isEmployeeTopicOpened: false,
      employeeTopic: INITIAL_TOPIC_DATA,
    });

  onSaveEmployeeTopic = topic => {
    const { translations, oneOnOneRecord, getOneOnOneRecord } = this.props;

    if (topic.id) {
      return http
        .patch(api_one_on_one_topic(topic.id), {
          name: topic.name,
        })
        .then(() => {
          showSuccessMessage(
            translations.successMessages.general.employeeTopicUpdated
          );

          return getOneOnOneRecord(oneOnOneRecord.id);
        });
    }

    return http
      .post(API_ONE_ON_ONE_TOPICS, { name: topic.name })
      .then(({ data }) =>
        http
          .patch(api_one_on_one_record_agenda(topic.meetingId), {
            agenda: [data.id],
          })
          .then(() => {
            showSuccessMessage(
              translations.successMessages.general.employeeTopicAdded
            );
            return getOneOnOneRecord(oneOnOneRecord.id);
          })
      );
  };

  onOpenEmployeeTopicDelete = topic =>
    this.setState({ employeeTopic: topic, isDeleteEmployeeTopicOpened: true });

  onCloseEmployeeTopicDelete = () =>
    this.setState({
      isDeleteEmployeeTopicOpened: false,
    });

  onDeleteCustomTopic = () => {
    const { translations, oneOnOneRecord, getOneOnOneRecord } = this.props;
    const { employeeTopic } = this.state;
    http
      .patch(api_one_on_one_record_agenda(employeeTopic.meetingId), {
        agenda: [],
      })
      .then(() => {
        this.onCloseEmployeeTopicDelete();
        this.onCloseEmployeeTopic();
        showSuccessMessage(
          translations.deleteOneOnOneTopicDialog.deleteSuccess
        );

        return getOneOnOneRecord(oneOnOneRecord.id);
      });
  };

  onCreateTag = async tagName => {
    const { addMultipleTags } = this.props;
    const tags = await onSaveCreatableTag(tagName);
    const [tag] = tags.create;

    addMultipleTags({ categoryId: GENERAL_CATEGORY_ID, tags });

    return tag;
  };

  onSaveRecord = async values => {
    const { translations, navigate, oneOnOneRecord, getOneOnOneRecord } =
      this.props;
    const { newRecurring } = this.state;
    const { user, hasCustomTopic, customTopic, ...rest } = values;
    const { AddOneOnOne } = EVENT_ACTION_TYPES;
    let newRecordId = null;
    const payload = { ...rest };

    if (hasCustomTopic) {
      const { data } = customTopic?.id
        ? await http.patch(api_one_on_one_topic(customTopic?.id), {
            name: customTopic?.name,
          })
        : await http.post(API_ONE_ON_ONE_TOPICS, {
            name: customTopic?.name,
          });

      if (data?.id) {
        payload.agenda = [...payload.agenda, data.id];
      }
    }

    if (oneOnOneRecord && !newRecurring) {
      await http.patch(api_one_on_one_record(oneOnOneRecord.id), {
        ...payload,
        user: user.id,
      });
    } else {
      const { data } = await http.post(api_user_one_on_one(user.id), {
        ...payload,
      });

      newRecordId = data.id;

      tagManagerDataLayer(AddOneOnOne.action, AddOneOnOne.name);
    }

    if (newRecordId) {
      navigate(`/1-1/${newRecordId}/details`, { replace: true });
    }

    showSuccessMessage(
      prepareManageOneOnOneSuccessMessages(
        translations.successMessages,
        oneOnOneRecord.id
      )
    );

    return getOneOnOneRecord(newRecordId || oneOnOneRecord.id);
  };

  toggleAddImpression =
    (cb = () => {}) =>
    () =>
      this.setState(
        prevState => ({
          isAddImpressionOpened: !prevState.isAddImpressionOpened,
        }),
        cb
      );

  onSaveImpression = async data => {
    const { translations, oneOnOneRecord, getOneOnOneRecord } = this.props;
    const { impression, nextRecurring, stoppedRecurring, notify, tasks } = data;
    const { recurring, tags, agenda, note, plans } = oneOnOneRecord;
    const { AddOneOnOne } = EVENT_ACTION_TYPES;
    const noteText = note ? note.text : '';

    return http
      .patch(api_one_on_one_record(oneOnOneRecord.id), {
        meeting_impression: impression,
        recurring: stoppedRecurring ? RECURRING_TYPES[0].id : recurring,
        user: oneOnOneRecord.user.id,
        tasks: prepareTasksForSave(tasks),
      })
      .then(async () => {
        showSuccessMessage(translations.addImpressionDialog.success);
        this.toggleAddImpression(() => getOneOnOneRecord(oneOnOneRecord.id))();
        if (!stoppedRecurring) {
          const params = {
            meeting_time: nextRecurring,
            recurring: stoppedRecurring ? RECURRING_TYPES[0].id : recurring,
            tags: getObjectToNumberArray(tags),
            plans: getObjectToNumberArray(plans),
            agenda: getObjectToNumberArray(agenda),
            note: noteText,
            user: oneOnOneRecord.user.id,
            notify,
          };
          await http.post(api_user_one_on_one(oneOnOneRecord.user.id), {
            ...params,
          });
          showSuccessMessage(translations.successMessages.general.create);
          tagManagerDataLayer(AddOneOnOne.action, AddOneOnOne.name);
        }
      }, this.toggleAddImpression());
  };

  onSaveAndCreate = async (impression, tasks) => {
    const { translations, oneOnOneRecord, getOneOnOneRecord } = this.props;

    await http
      .patch(api_one_on_one_record(oneOnOneRecord.id), {
        meeting_impression: impression,
        tasks: prepareTasksForSave(tasks),
        user: oneOnOneRecord.user.id,
      })
      .then(() => {
        showSuccessMessage(translations.addImpressionDialog.success);
        getOneOnOneRecord(oneOnOneRecord.id);
        this.toggleAddImpression()();
        this.setState({
          isManageRecordOpened: true,
          newRecurring: true,
        });
      }, this.toggleAddImpression());
  };

  toggleDeleteRecord =
    (cb = () => {}) =>
    () => {
      this.setState(
        prevState => ({
          isDeleteRecordOpened: !prevState.isDeleteRecordOpened,
        }),
        cb
      );
    };

  onDeleteRecord = () => {
    const { translations, oneOnOneRecord } = this.props;

    return http.delete(api_one_on_one_record(oneOnOneRecord.id)).then(() => {
      showSuccessMessage(translations.deleteOneOnOneRecordDialog.deleteSuccess);
      this.toggleDeleteRecord(this.onRedirectToRecordsPage)();
    }, this.toggleDeleteRecord());
  };

  openEditComment = commentId => {
    this.setState({ commentId });
  };

  closeEditComment = () => {
    this.setState({ commentId: 0 });
  };

  handleSendComment = (comment, isEdit) => {
    const { oneOnOneRecord, getOneOnOneComments } = this.props;
    const { commentId } = this.state;

    if (isEdit) {
      return http
        .patch(api_one_on_one_comment_update(oneOnOneRecord.id, commentId), {
          comment,
        })
        .then(() => {
          this.setState({
            page: DEFAULT_PARAMS.page,
            commentId: 0,
          });
          getOneOnOneComments(oneOnOneRecord.id, DEFAULT_PARAMS);
        });
    }

    return http
      .post(api_one_on_one_comments(oneOnOneRecord.id), { comment })
      .then(() => {
        getOneOnOneComments(oneOnOneRecord.id, DEFAULT_PARAMS);
        this.setState({ page: DEFAULT_PARAMS.page });
      });
  };

  handleDeleteComment = commentId => {
    const { translations, oneOnOneRecord, getOneOnOneComments } = this.props;

    return http
      .delete(api_one_on_one_comment_update(oneOnOneRecord.id, commentId))
      .then(() => {
        showSuccessMessage(
          translations.comment.deleteCommentDialog.deleteSuccess
        );
        this.setState({ page: DEFAULT_PARAMS.page });
        getOneOnOneComments(oneOnOneRecord.id, DEFAULT_PARAMS);
      });
  };

  onLoadMore = () => {
    const { oneOnOneRecord, getOneOnOneComments } = this.props;
    const { page } = this.state;
    const incrementedPage = page + 1;
    const params = replacePropsInObject(DEFAULT_PARAMS, {
      page: incrementedPage,
    });
    this.setState({ page: incrementedPage });
    return getOneOnOneComments(oneOnOneRecord.id, params);
  };

  handleOpenManageTasks = () => this.setState({ isManageTasksOpened: true });

  handleCloseManageTasks = () => this.setState({ isManageTasksOpened: false });

  onSaveTasks = async values => {
    const { translations, auth, getOneOnOneRecord, oneOnOneRecord } =
      this.props;
    const isCreatedForCurrentUser = auth.id === oneOnOneRecord.user.id;
    const tasks = prepareTasksForSave(values.tasks);
    const data = {
      tasks,
      user: oneOnOneRecord.user.id,
    };

    if (isCreatedForCurrentUser) {
      await http.post(api_one_on_one_tasks(oneOnOneRecord.id), data);
    } else {
      await http.patch(api_one_on_one_record(oneOnOneRecord.id), data);
    }
    showSuccessMessage(translations.manageTasks.successMessage);

    return getOneOnOneRecord(oneOnOneRecord.id);
  };

  checkCanManageTask = task => {
    const { auth, oneOnOneRecord } = this.props;

    return oneOnOneRecord.moderator?.id === auth.id || task.user.id === auth.id;
  };

  handleTaskStatusChange = (record, task) => {
    const { changeOneOnOneTaskStatus } = this.props;

    return changeOneOnOneTaskStatus(record.id, task.id);
  };

  goToUserProfilePage = userId => this.props.navigate(`/people/${userId}`);

  canAccessUserProfileCheck = user => {
    const { auth, accessControl } = this.props;

    return isUserProfileAccessibleCheck(
      accessControl,
      auth.id,
      user?.id || user
    );
  };

  getUsersWithViewPermission = recordId => () =>
    http.get(api_one_on_one_with_notes_view_permission(recordId));

  handleClearNotifications = () => {
    const { location, navigate } = this.props;

    return navigate(location.pathname, {
      replace: true,
      state: null,
    });
  };

  onDownloadPdf = async () => {
    const {
      matchedOneOnOneId,
      oneOnOneRecord,
      oneOnOneComments,
      getOneOnOneComments,
    } = this.props;
    const { commentId } = this.state;
    const { user, meeting_time } = oneOnOneRecord;
    const hasHiddenComments =
      oneOnOneComments.count > DEFAULT_PARAMS[PARAMS.PAGE_SIZE];
    if (commentId) {
      this.closeEditComment();
    }
    try {
      if (hasHiddenComments) {
        await getOneOnOneComments(matchedOneOnOneId, {
          ...DEFAULT_PARAMS,
          [PARAMS.PAGE_SIZE]: 100,
        });
      }
      const content = this.contentRef.current;
      const clonedContent = content.cloneNode(true);
      const unwantedElements = clonedContent.querySelectorAll('.no-print');
      unwantedElements.forEach(el => el.remove());
      const opt = {
        margin: [0.5, 0.5, 0.5, 0.5],
        filename: `1:1_meeting_${user.first_name}_${
          user.last_name
        }_${formatDate(meeting_time, DATE_TIME_FORMAT)}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, scrollY: 0 },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all'] },
      };
      await html2pdf().set(opt).from(clonedContent).save();
    } finally {
      if (hasHiddenComments) {
        this.setState({ page: DEFAULT_PARAMS.page });
        await getOneOnOneComments(matchedOneOnOneId, DEFAULT_PARAMS);
      }
    }
  };

  canManageCommentCheck = currentComment => {
    const { auth, accessControl } = this.props;

    return hasScopedAuthPermissionCheck(
      PERMISSION_CODES.ONE_ON_ONE_COMMENTS_MANAGE,
      accessControl,
      auth.id,
      currentComment?.creator?.id
    );
  };

  render() {
    const {
      classes,
      translations,
      location,
      auth,
      accessControl,
      oneOnOneRecord,
      topics,
      categories,
      oneOnOneComments,
    } = this.props;
    const {
      isLoading,
      isManageRecordOpened,
      isManageTasksOpened,
      isReschedule,
      isEmployeeTopicOpened,
      employeeTopic,
      newRecurring,
      isDeleteRecordOpened,
      isDeleteEmployeeTopicOpened,
      isAddImpressionOpened,
      commentId,
    } = this.state;
    const { user, note, meeting_time, requested } = oneOnOneRecord;
    const canEdit = hasScopedAuthPermissionCheck(
      PERMISSION_CODES.ONE_ON_ONE_EDIT,
      accessControl,
      auth.id,
      user?.id
    );
    const canDelete = hasScopedAuthPermissionCheck(
      PERMISSION_CODES.ONE_ON_ONE_DELETE,
      accessControl,
      auth.id,
      user?.id
    );
    const canViewPlans = hasScopedAuthPermissionCheck(
      PERMISSION_CODES.PLANS_VIEW,
      accessControl,
      auth?.id,
      user?.id
    );

    const notificationsData = location?.state?.notifications
      ? prepareNotificationUpdates(
          translations.notifications.updates,
          location.state.notifications.model,
          location.state.notifications.updates
        )
      : null;
    const isUserActive = user && !isUserDeactivated(user);
    const showLoadMore = !!oneOnOneComments?.next;
    const isCurrentUserModerator = auth.id === oneOnOneRecord?.moderator?.id;
    const isPendingRequest = requested && meeting_time === null;
    const hasNotifications = !!notificationsData;
    const hasDownloadButton =
      oneOnOneRecord?.meeting_time &&
      isDateInPast(oneOnOneRecord.meeting_time) &&
      oneOnOneRecord?.has_impression &&
      canEdit;

    return (
      <div>
        {!isLoading && (
          <div ref={this.contentRef}>
            <div className={classNames(classes.heading, 'no-print')}>
              <SectionTitle title={translations.details} />
              {hasDownloadButton && (
                <div className={classes.downloadButtonWrapper}>
                  <ActionButton
                    iconButtonClass={classes.downloadButton}
                    type={ACTION_BUTTON_TYPES.DOWNLOAD}
                    tooltipText={
                      translations.record.actionButtons[
                        ACTION_BUTTON_TYPES.DOWNLOAD.toLowerCase()
                      ]
                    }
                    onClickHandler={this.onDownloadPdf}
                  />
                </div>
              )}
              <UpdatesInfo
                translations={translations.notifications}
                updates={notificationsData}
                isUpdate={location?.state?.notifications?.isUpdate}
                hasUpdates={hasNotifications}
                onClearAll={this.handleClearNotifications}
              />
            </div>
            <OneOnOneRecordDetails
              translations={translations.record}
              record={oneOnOneRecord}
              notifications={location?.state?.notifications}
              isPendingRequest={isPendingRequest}
              isCurrentUserSubject={auth.id === user?.id}
              isCurrentUserModerator={isCurrentUserModerator}
              isUserActive={isUserActive}
              canEdit={canEdit}
              canDelete={canDelete}
              canViewPlans={canViewPlans}
              checkCanAccessUserProfile={this.canAccessUserProfileCheck}
              checkCanManageTask={this.checkCanManageTask}
              onTaskStatusChange={this.handleTaskStatusChange}
              goToUserProfilePage={this.goToUserProfilePage}
              onManageTasks={this.handleOpenManageTasks}
              onAddImpression={this.toggleAddImpression()}
              onReschedule={this.toggleManageRecord(true)}
              onDeleteRecord={this.toggleDeleteRecord()}
              onEditRecord={this.toggleManageRecord()}
              onEditEmployeeTopic={this.onOpenEmployeeTopic}
            />
            {!isPendingRequest && (
              <>
                {isCurrentUserModerator && (
                  <>
                    <div
                      className={classNames(
                        classes.noteLabelWrapper,
                        'no-print'
                      )}
                    >
                      <SectionTitle
                        className={classes.subtitle}
                        title={translations.privateNote}
                        variant="h5"
                      />
                      <Tooltip
                        customIconClass={classes.privateNoteIcon}
                        text={translations.privateNoteTooltip}
                        icon={AnonymousIcon}
                      />
                    </div>
                    {note ? (
                      <div className={classNames(classes.notes, 'no-print')}>
                        <RichTextPreview text={note.text} />
                        {isUserActive && (
                          <ActionButton
                            className={classes.editDescriptionButton}
                            type={ACTION_BUTTON_TYPES.EDIT}
                            onClickHandler={this.toggleManageRecord()}
                          />
                        )}
                      </div>
                    ) : (
                      <NotificationCard
                        className="no-print"
                        title={translations.noPrivateNote.title}
                        content={translations.noPrivateNote.message}
                      />
                    )}
                  </>
                )}
                {!isPendingRequest ? (
                  <div>
                    {!isArrayEmpty(oneOnOneComments.results) && (
                      <div
                        className={classNames(
                          classes.subtitle,
                          classes.noteLabelWrapper,
                          { 'no-print': isArrayEmpty(oneOnOneComments.results) }
                        )}
                      >
                        <SectionTitle
                          title={translations.meetingNotes}
                          variant="h5"
                        />
                        <UserCount
                          className={classNames(classes.userCount, 'no-print')}
                          icon={PreviewUserIcon}
                          goToUserProfile={this.goToUserProfilePage}
                          canAccessUserProfileCheck={
                            this.canAccessUserProfileCheck
                          }
                          tooltipTitle={
                            translations.record.meetingNotesUsersListTitle
                          }
                          tooltipLabel={
                            translations.record?.meetingNotesUsersListTooltip
                          }
                          getUsers={this.getUsersWithViewPermission(
                            oneOnOneRecord.id
                          )}
                          hasUserCount={false}
                          isPreviewIcon
                        />
                      </div>
                    )}
                    <UsersComments
                      translations={translations.comment}
                      currentUser={auth}
                      commentId={commentId}
                      comments={oneOnOneComments.results}
                      canEditCommentCheck={this.canManageCommentCheck}
                      canDeleteCommentCheck={this.canManageCommentCheck}
                      onSendComment={this.handleSendComment}
                      openEditComment={this.openEditComment}
                      onDeleteComment={this.handleDeleteComment}
                      closeEditComment={this.closeEditComment}
                      isRichText
                    />
                  </div>
                ) : null}
                <div
                  className={classNames(classes.loadMoreContainer, 'no-print')}
                >
                  {showLoadMore && (
                    <CustomButton
                      className={classes.loadMore}
                      type="addWithTextRounded"
                      onClick={this.onLoadMore}
                    >
                      {translations.loadMore}
                    </CustomButton>
                  )}
                </div>
              </>
            )}
          </div>
        )}
        <ManageOneOnOne
          translations={translations.oneOnOneRecordDialog}
          isOpened={isManageRecordOpened}
          topics={topics}
          record={oneOnOneRecord}
          createdFor={user}
          auth={auth}
          accessControl={accessControl}
          isOneOnOneRequest={isPendingRequest}
          isReschedule={isReschedule}
          newRecurring={newRecurring}
          categories={categories}
          onCreateTag={this.onCreateTag}
          onDelete={this.toggleDeleteRecord()}
          onClose={this.toggleManageRecord()}
          onSave={this.onSaveRecord}
        />
        <CustomFormDrawer
          translations={prepareUserTopicSidebarTranslations(
            translations.manageTopicForm,
            !!employeeTopic?.name
          )}
          isOpened={isEmployeeTopicOpened}
          isInitialValid={!!employeeTopic?.id}
          initialData={employeeTopic}
          hideDelete={!employeeTopic.id}
          fields={prepareUserTopicFields()}
          onClose={this.onCloseEmployeeTopic}
          onSave={this.onSaveEmployeeTopic}
          onDelete={this.onOpenEmployeeTopicDelete}
        />
        <ManageTasks
          translations={translations.manageTasks}
          isOpened={isManageTasksOpened}
          record={oneOnOneRecord}
          availableUsers={prepareOneOnOneTasksAvailableUsers(oneOnOneRecord)}
          onClose={this.handleCloseManageTasks}
          onSave={this.onSaveTasks}
        />
        <AddImpressionDialog
          translations={translations.addImpressionDialog}
          isOpened={isAddImpressionOpened}
          currentRecord={isAddImpressionOpened ? oneOnOneRecord : null}
          recordId={isAddImpressionOpened ? oneOnOneRecord.id : null}
          onCancel={this.toggleAddImpression()}
          onConfirm={this.onSaveImpression}
          onSaveAndCreate={this.onSaveAndCreate}
        />
        <AlertDialog
          translations={translations.deleteOneOnOneRecordDialog}
          isOpened={isDeleteRecordOpened}
          onClose={this.toggleDeleteRecord()}
          onConfirm={this.onDeleteRecord}
          isWarning
        />
        <AlertDialog
          translations={translations.deleteOneOnOneTopicDialog}
          isOpened={isDeleteEmployeeTopicOpened}
          onClose={this.onCloseEmployeeTopicDelete}
          onConfirm={this.onDeleteCustomTopic}
          isWarning
        />
      </div>
    );
  }
}

OneOnOneDetailsPage.propTypes = {
  classes: PropTypes.object.isRequired,
  translations: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  oneOnOneComments: PropTypes.shape({}).isRequired,
  matchedOneOnOneId: PropTypes.string.isRequired,
  navigate: PropTypes.func.isRequired,
  oneOnOneRecord: PropTypes.object.isRequired,
  topics: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  getOneOnOneRecord: PropTypes.func.isRequired,
  getOneOnOneTopics: PropTypes.func.isRequired,
  getTagsCategories: PropTypes.func.isRequired,
  addMultipleTags: PropTypes.func.isRequired,
  changeOneOnOneTaskStatus: PropTypes.func.isRequired,
  clearOneOnOneRecord: PropTypes.func.isRequired,
  clearOneOnOneTopics: PropTypes.func.isRequired,
  clearTagsCategories: PropTypes.func.isRequired,
  clearOneOnOneComments: PropTypes.func.isRequired,
};

export default withStyles(styles)(OneOnOneDetailsPage);
